import clsx from "clsx";
import type {
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormRegister,
} from "react-hook-form";

import { BrandIcon } from "@shared/ui/brand-icon";
import { Button } from "@shared/ui/button";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Icon } from "@shared/ui/icon";
import { LoadingSpinner } from "@shared/ui/loading-spinner";
import { Body } from "@shared/ui/typography";

import s from "./ai-assistant.module.scss";

type Inputs = {
  userPrompt: string;
};

type AIAssistantBottomProps = {
  defaultPrompts: { id: number; text: string }[];
  chatDataLength: number;
  hasAnswer: boolean;
  hasValue: boolean;
  individualStep: string | null;
  canGoBack: boolean;
  isSubmited: boolean;
  isLastStep: boolean;
  isLoading: boolean;
  isNextDisabled: boolean;
  register: UseFormRegister<Inputs>;
  handleSubmit: UseFormHandleSubmit<Inputs>;
  onSubmit: SubmitHandler<Inputs>;
  onPromptClick: (text: string) => void;
  onClickOnIndividual: () => void;
  onBackToChat: () => void;
  onBackToPrevStep: () => void;
  onNextQuestion: () => void;
  onGetProgram: () => void;
  onInputValueChange: (hasValue: boolean) => void;
};

export const AIAssistantBottom = (props: AIAssistantBottomProps) => {
  const {
    defaultPrompts,
    chatDataLength,
    handleSubmit,
    hasAnswer,
    hasValue,
    individualStep,
    isSubmited,
    canGoBack,
    isLastStep,
    isLoading,
    isNextDisabled,
    onBackToChat,
    onBackToPrevStep,
    onClickOnIndividual,
    onGetProgram,
    onInputValueChange,
    onNextQuestion,
    onPromptClick,
    onSubmit,
    register,
  } = props;

  const slicedPrompts =
    defaultPrompts.length > 3 ? defaultPrompts.slice(0, 3) : defaultPrompts;

  return (
    <div className={clsx(s.bottom, { [s.disabled]: isLoading })}>
      <div className={clsx(s.loading, { [s.viewLoader]: isLoading })}>
        <LoadingSpinner />
        <Body size="caption" weight="medium">
          AI-помощник думает
        </Body>
      </div>
      {!hasAnswer && (
        <div className={s.examplePromts}>
          <Button
            onClick={onClickOnIndividual}
            className={s.relationButton}
            aria-label="relation-button"
          >
            <Icon name="relation" className={s.relationIcon} />
            <Body size="caption" weight="semiBold">
              Индивидуальная программа
            </Body>
          </Button>
          {slicedPrompts.map((item) => (
            <Button
              key={`prompt_${item.id}`}
              onClick={() => onPromptClick(item.text)}
              className={s.examplePromt}
              aria-label="prompt-button"
            >
              <Body size="caption" weight="semiBold">
                {item.text}
              </Body>
            </Button>
          ))}
        </div>
      )}
      {individualStep !== null && (
        <div className={s.buttonsWrapper}>
          <Button className={s.buttonChat} onClick={onBackToChat}>
            <Icon name="chat" className={s.chatIcon} />
          </Button>
          <div className={clsx(s.buttons, { [s.hideButton]: isSubmited })}>
            <Button
              className={clsx(s.button, {
                [s.hideButton]: !canGoBack,
              })}
              onClick={onBackToPrevStep}
            >
              <Icon name="arrow-left" className={s.backIcon} />
              <Body
                size="uppercase-small"
                weight="semiBold"
                className={s.backText}
              >
                назад
              </Body>
            </Button>

            {!isLastStep && (
              <ButtonWithIcon
                iconName="arrow"
                iconFillColor="cadmium"
                variant="secondary-dark"
                className={s.nextButton}
                disabled={isNextDisabled}
                onClick={onNextQuestion}
              >
                <Body size="uppercase-small" weight="semiBold">
                  следующий вопрос
                </Body>
              </ButtonWithIcon>
            )}
            {isLastStep && (
              <ButtonWithIcon
                iconName="arrow"
                iconFillColor="cadmium"
                variant="secondary-dark"
                className={s.nextButton}
                onClick={onGetProgram}
              >
                <Body size="uppercase-small" weight="semiBold">
                  Получить программу
                </Body>
              </ButtonWithIcon>
            )}
          </div>
        </div>
      )}
      {individualStep === null && (
        <>
          <form
            className={clsx(s.inputContainer, {
              [s.active]: hasValue,
              [s.fullWitdh]: chatDataLength > 0,
            })}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className={s.inputWrap}>
              <input
                type="text"
                placeholder="Задайте любой вопрос"
                className={s.input}
                {...register("userPrompt", {
                  required: true,
                  onChange: ({ target }) =>
                    onInputValueChange(target.value.length > 0),
                })}
              />
            </div>
            <Button
              className={s.iconWrap}
              type="submit"
              aria-label="submit-button"
            >
              <BrandIcon name="up" fillColor="violet" strokeColor="white" />
            </Button>
          </form>
          {hasAnswer && (
            <Button
              className={s.buttonIndividual}
              onClick={onClickOnIndividual}
            >
              <Icon name="relation" className={s.chatIcon} />
            </Button>
          )}
        </>
      )}
    </div>
  );
};
